import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from "react";
import { useTranslations } from "next-intl";
import { cn } from "@/lib/utils";
import { RealEstate } from "@/types/real-estate";
import { Separator } from "@/components/ui/separator";
import { EnergeticCertificate } from "../EnergeticCertificate";
import { AttributeCard } from "../attribute-card/AttributeCard";
import { useRealEstateAttributes } from "./useRealEstateAttributes";
type RealEstateAttributesProps = {
  detailData: RealEstate;
  realEstateType?: string;
  /** Wrap the row in a bordered card (no separators) instead of the
   *  separator-divided row — used on the full-page detail. */
  bordered?: boolean;
};
export function RealEstateAttributes(t0) {
  const $ = _c(86);
  const {
    detailData,
    realEstateType,
    bordered
  } = t0;
  const t = useTranslations("modules.realEstateDetail");
  const {
    roomData,
    genderLabel,
    genderColor,
    roomTypeLabel,
    showEnergyCert,
    showArea,
    showPricePerSqm,
    showType,
    hasEnergyCert,
    areaLabel,
    typeLabel,
    pricePerSqm,
    availabilityLabel,
    depositLabel
  } = useRealEstateAttributes(detailData, realEstateType);
  let items;
  if ($[0] !== areaLabel || $[1] !== availabilityLabel || $[2] !== depositLabel || $[3] !== detailData || $[4] !== genderColor || $[5] !== genderLabel || $[6] !== hasEnergyCert || $[7] !== pricePerSqm || $[8] !== roomData || $[9] !== roomTypeLabel || $[10] !== showArea || $[11] !== showEnergyCert || $[12] !== showPricePerSqm || $[13] !== showType || $[14] !== t || $[15] !== typeLabel) {
    items = [];
    if (showEnergyCert) {
      let t1;
      if ($[17] !== t) {
        t1 = t("energeticCertificate");
        $[17] = t;
        $[18] = t1;
      } else {
        t1 = $[18];
      }
      let t2;
      if ($[19] !== detailData || $[20] !== hasEnergyCert) {
        t2 = hasEnergyCert ? <EnergeticCertificate value={detailData.energyCertificationType} /> : <span className="text-lg font-semibold text-foreground-heading">-</span>;
        $[19] = detailData;
        $[20] = hasEnergyCert;
        $[21] = t2;
      } else {
        t2 = $[21];
      }
      let t3;
      if ($[22] !== t1 || $[23] !== t2) {
        t3 = {
          label: t1,
          value: t2
        };
        $[22] = t1;
        $[23] = t2;
        $[24] = t3;
      } else {
        t3 = $[24];
      }
      items.push(t3);
    }
    if (showArea) {
      let t1;
      if ($[25] !== t) {
        t1 = t("area");
        $[25] = t;
        $[26] = t1;
      } else {
        t1 = $[26];
      }
      let t2;
      if ($[27] !== areaLabel) {
        t2 = <span className="text-lg font-semibold text-foreground-heading">{areaLabel}</span>;
        $[27] = areaLabel;
        $[28] = t2;
      } else {
        t2 = $[28];
      }
      let t3;
      if ($[29] !== t1 || $[30] !== t2) {
        t3 = {
          label: t1,
          value: t2
        };
        $[29] = t1;
        $[30] = t2;
        $[31] = t3;
      } else {
        t3 = $[31];
      }
      items.push(t3);
    }
    if (showPricePerSqm) {
      let t1;
      if ($[32] !== t) {
        t1 = t("pricePerSqm");
        $[32] = t;
        $[33] = t1;
      } else {
        t1 = $[33];
      }
      let t2;
      if ($[34] !== pricePerSqm || $[35] !== t) {
        t2 = pricePerSqm ? <div className="flex gap-2 text-lg text-foreground-heading"><span className="font-semibold">{pricePerSqm}</span><span className="font-semibold text-foreground-heading">{t("euroPricePerSqm")}</span></div> : <span className="text-lg font-semibold text-foreground-heading">-</span>;
        $[34] = pricePerSqm;
        $[35] = t;
        $[36] = t2;
      } else {
        t2 = $[36];
      }
      let t3;
      if ($[37] !== t1 || $[38] !== t2) {
        t3 = {
          label: t1,
          value: t2
        };
        $[37] = t1;
        $[38] = t2;
        $[39] = t3;
      } else {
        t3 = $[39];
      }
      items.push(t3);
    }
    if (showType) {
      let t1;
      if ($[40] !== t) {
        t1 = t("type");
        $[40] = t;
        $[41] = t1;
      } else {
        t1 = $[41];
      }
      let t2;
      if ($[42] !== typeLabel) {
        t2 = <span className="text-lg font-semibold text-foreground-heading capitalize">{typeLabel}</span>;
        $[42] = typeLabel;
        $[43] = t2;
      } else {
        t2 = $[43];
      }
      let t3;
      if ($[44] !== t1 || $[45] !== t2) {
        t3 = {
          label: t1,
          value: t2
        };
        $[44] = t1;
        $[45] = t2;
        $[46] = t3;
      } else {
        t3 = $[46];
      }
      items.push(t3);
    }
    if (roomData?.gender) {
      let t1;
      if ($[47] !== t) {
        t1 = t("gender");
        $[47] = t;
        $[48] = t1;
      } else {
        t1 = $[48];
      }
      let t2;
      if ($[49] !== genderColor) {
        t2 = cn("text-lg font-semibold", genderColor);
        $[49] = genderColor;
        $[50] = t2;
      } else {
        t2 = $[50];
      }
      let t3;
      if ($[51] !== genderLabel || $[52] !== t2) {
        t3 = <span className={t2}>{genderLabel}</span>;
        $[51] = genderLabel;
        $[52] = t2;
        $[53] = t3;
      } else {
        t3 = $[53];
      }
      let t4;
      if ($[54] !== t1 || $[55] !== t3) {
        t4 = {
          label: t1,
          value: t3
        };
        $[54] = t1;
        $[55] = t3;
        $[56] = t4;
      } else {
        t4 = $[56];
      }
      items.push(t4);
    }
    if (availabilityLabel) {
      let t1;
      if ($[57] !== t) {
        t1 = t("availability");
        $[57] = t;
        $[58] = t1;
      } else {
        t1 = $[58];
      }
      let t2;
      if ($[59] !== availabilityLabel) {
        t2 = <span className="text-lg font-semibold text-foreground-heading">{availabilityLabel}</span>;
        $[59] = availabilityLabel;
        $[60] = t2;
      } else {
        t2 = $[60];
      }
      let t3;
      if ($[61] !== t1 || $[62] !== t2) {
        t3 = {
          label: t1,
          value: t2
        };
        $[61] = t1;
        $[62] = t2;
        $[63] = t3;
      } else {
        t3 = $[63];
      }
      items.push(t3);
    }
    if (roomData) {
      let t1;
      if ($[64] !== t) {
        t1 = t("roomType");
        $[64] = t;
        $[65] = t1;
      } else {
        t1 = $[65];
      }
      let t2;
      if ($[66] !== roomTypeLabel) {
        t2 = <span className="text-lg font-semibold text-foreground-heading">{roomTypeLabel}</span>;
        $[66] = roomTypeLabel;
        $[67] = t2;
      } else {
        t2 = $[67];
      }
      let t3;
      if ($[68] !== t1 || $[69] !== t2) {
        t3 = {
          label: t1,
          value: t2
        };
        $[68] = t1;
        $[69] = t2;
        $[70] = t3;
      } else {
        t3 = $[70];
      }
      items.push(t3);
    }
    if (roomData) {
      let t1;
      if ($[71] !== t) {
        t1 = t("deposit");
        $[71] = t;
        $[72] = t1;
      } else {
        t1 = $[72];
      }
      let t2;
      if ($[73] !== depositLabel) {
        t2 = <span className="text-lg font-semibold text-foreground-heading">{depositLabel}</span>;
        $[73] = depositLabel;
        $[74] = t2;
      } else {
        t2 = $[74];
      }
      let t3;
      if ($[75] !== t1 || $[76] !== t2) {
        t3 = {
          label: t1,
          value: t2
        };
        $[75] = t1;
        $[76] = t2;
        $[77] = t3;
      } else {
        t3 = $[77];
      }
      items.push(t3);
    }
    $[0] = areaLabel;
    $[1] = availabilityLabel;
    $[2] = depositLabel;
    $[3] = detailData;
    $[4] = genderColor;
    $[5] = genderLabel;
    $[6] = hasEnergyCert;
    $[7] = pricePerSqm;
    $[8] = roomData;
    $[9] = roomTypeLabel;
    $[10] = showArea;
    $[11] = showEnergyCert;
    $[12] = showPricePerSqm;
    $[13] = showType;
    $[14] = t;
    $[15] = typeLabel;
    $[16] = items;
  } else {
    items = $[16];
  }
  if (items.length === 0) {
    return null;
  }
  const t1 = bordered ? "gap-3 pb-1" : "mt-3";
  let t2;
  if ($[78] !== t1) {
    t2 = cn("flex w-full items-stretch px-1", t1);
    $[78] = t1;
    $[79] = t2;
  } else {
    t2 = $[79];
  }
  let t3;
  if ($[80] !== bordered || $[81] !== items) {
    t3 = items.map((item, i) => <div key={item.label} className={cn("flex flex-1 min-w-0 items-stretch gap-x-5", bordered && "rounded-lg border border-border px-4 py-3")}>{!bordered && i > 0 && <Separator orientation="vertical" className="h-auto" />}<AttributeCard label={item.label}>{item.value}</AttributeCard></div>);
    $[80] = bordered;
    $[81] = items;
    $[82] = t3;
  } else {
    t3 = $[82];
  }
  let t4;
  if ($[83] !== t2 || $[84] !== t3) {
    t4 = <div className={t2} data-sentry-component="RealEstateAttributes" data-sentry-source-file="RealEstateAttributes.tsx">{t3}</div>;
    $[83] = t2;
    $[84] = t3;
    $[85] = t4;
  } else {
    t4 = $[85];
  }
  return t4;
}